import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, Location, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import {
  Category_BigCategory,
  Category_BigCategory_MediumCategory,
  Category_BigCategory_MediumCategory_SmallCategory,
} from '@proto/store/api/store/category';
import { MarketingCategoryModel } from '@common/marketing-category/marketing-category.model';
import { MenuCartComponent } from '../../../cart/desktop/menu-cart/menu-cart.component';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { EmptySearchModel } from '@common/empty-search/empty-search.model';
import { ConfirmData, ConfirmDialogComponent } from '@common/confirm-dialog/confirm-dialog.component';
import { BottomInfoBarComponent } from '@common/bottom-info-bar/bottom-info-bar.component';
import { Links } from '@helper/links';
import { Constants } from '@helper/constants';
import { SSRExcludeDirective } from '@helper/ssr-exclude.directive';
import { Home_SpecialEffect, SalesEvent_Event } from '@proto/store/api/store/home';
import { AuthStateService } from '@service/auth-state.service';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { RecordHitsService } from '@service/record-hits.service';
import { CustomerServiceComponent } from '../../../customer-service/desktop/customer-service.component';
import { CsDeferService } from '@service/cs-defer.service';
import { EventTracker } from '../../../../analytics/event-tracker.service';
import { SearchMenuComponent } from '../../../search/desktop/search-menu/search-menu.component';
import { ListOrdersRequest_State } from '@proto/store/api/store/orders/orders';
import { SalesEventModel } from '../../../sales-event/sales-event.model';
import { HomeModel } from '../../../home/home.model';
import { CategoryLinkPipe, MiniHomeLinkPipe } from '@common/pipes';
import { EventLinkPipe } from '@common/marketing-category/pipes';

import { filter } from 'rxjs';

type UserTab =
  | RoutePaths.NOTIFICATION
  | RoutePaths.FAVORITE
  | RoutePaths.ORDERS
  | RoutePaths.REFUND_ACCOUNT
  | RoutePaths.PROFILE;

@Component({
  imports: [
    NgForOf,
    NgIf,
    ImageLoadingComponent,
    MenuCartComponent,
    MatAutocompleteModule,
    NgTemplateOutlet,
    BottomInfoBarComponent,
    SSRExcludeDirective,
    FocusRemoverDirective,
    MatMenuModule,
    MatTooltipModule,
    CustomerServiceComponent,
    SearchMenuComponent,
    EventLinkPipe,
    RouterLink,
    CategoryLinkPipe,
    MiniHomeLinkPipe,
  ],
  selector: 'app-marketing-category',
  templateUrl: './marketing-category.component.html',
  styleUrls: ['./marketing-category.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingCategoryComponent implements OnInit, AfterViewInit {
  readonly width = Constants.APP_CONTAINER_WIDTH;
  readonly floatingMargin: string;
  readonly headerHeight = Constants.DESKTOP_HEADER_HEIGHT;
  readonly paths = RoutePaths;
  readonly fbLink = 'https://www.facebook.com/lativ.tw';
  readonly igLink = 'https://www.instagram.com/lativ_ig';
  readonly lineLink = 'https://page.line.me/lativ?openQrModal=true';
  readonly showSnow = computed(() => this.homeModel.snowEffect() === Home_SpecialEffect.SNOW);
  readonly outfitLink = `/${RouteHelper.outfit()}`;

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  @Input() showCategory = false;
  @Input() hiddenTop = false;
  @Input({ required: true }) showBottomBar = false;
  @Input() template?: TemplateRef<any>;
  @Input() isUserPage = false;
  @Input() getCategories = true;
  @Input() getHome = true;

  @Output() readonly heightChange = new EventEmitter<number>();
  @Output() readonly navigateToPath = new EventEmitter<UserTab>();

  @ViewChild('categoryHeader', { static: false }) categoryHeader!: ElementRef;

  constructor(
    public viewModel: MarketingCategoryModel,
    public authService: AuthStateService,
    public emptySearchModel: EmptySearchModel,
    public csDeferService: CsDeferService,
    private dialog: MatDialog,
    private location: Location,
    private eventTracker: EventTracker,
    private recordHits: RecordHitsService,
    private salesEventViewModel: SalesEventModel,
    private homeModel: HomeModel,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.floatingMargin = `translate(${this.width / 2 + 25}px)`;
  }

  @HostListener('window:scroll') onScroll(): void {
    this.viewModel.scrollPosition.set(window.scrollY);
  }

  ngOnInit(): void {
    if (this.showCategory) {
      this.viewModel.getSalesEvent(this.destroyRef);
    }
    if ((this.showCategory && this.getCategories) || !this.viewModel.apiResult().bigCategories.length) {
      this.viewModel.getCategories(this.destroyRef);
    }
    if (this.getHome && !this.homeModel.apiResult().banner?.banners.length) {
      this.homeModel.getHome(this.destroyRef);
    }
    this.emptySearchModel.listSearchKeywords(this.destroyRef);
    if (isPlatformBrowser(this.platformId) && !this.salesEventViewModel.hasCategories()) {
      // prefetch sales event when empty
      const trackingId = Links.trackingId(this.route.snapshot.queryParams);
      if (trackingId) {
        // 有 adId 就不要 preload.
        return;
      }
      this.salesEventViewModel.preLoad(this.destroyRef);
    }
  }

  ngAfterViewInit(): void {
    if (this.categoryHeader) {
      this.heightChange.emit(this.categoryHeader.nativeElement.offsetHeight);
    }
  }

  onChangeCategory(bigName: string, event: MouseEvent): void {
    if (
      this.location.path().includes(bigName) &&
      bigName === this.viewModel.currentBigId() &&
      !this.viewModel.currentSmallId()
    ) {
      event.stopPropagation();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    this.recordHits.categoryClick(bigName);
    this.viewModel.resetCategories();
  }

  goStyle(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    this.viewModel.resetCategories();
  }

  onSmallChange(mediumId: string, smallId: string, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    if (this.viewModel.currentMediumId() !== mediumId) {
      this.viewModel.currentMediumId.set(mediumId);
    }
    this.viewModel.currentSmallId.set(smallId);
  }

  goHome(): void {
    this.recordHits.logoClick();
    this.viewModel.goHome();
  }

  openSalesEvent(event: SalesEvent_Event, mouseEvent: MouseEvent): void {
    if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.shiftKey) {
      return;
    }
    this.eventTracker.onPromotion(event.id, `${event.title}．${event.subTitle}`);
  }

  login(): void {
    this.router.navigate([RouteHelper.login()]);
  }

  logout(): void {
    ConfirmDialogComponent.open(
      this.dialog,
      new ConfirmData({
        content: $localize`您確定要登出嗎？`,
        leftBtnTitle: $localize`取消`,
        rightBtnTitle: $localize`確定`,
        disableClose: true,
      })
    )
      .afterClosed()
      .pipe(
        filter(res => !!res),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.authService.logout();
        this.viewModel.resetCategories();
      });
  }

  toPath(path: UserTab): void {
    if (this.isUserPage) {
      this.navigateToPath.emit(path);
    } else {
      switch (path) {
        case RoutePaths.NOTIFICATION: {
          this.router.navigate([RouteHelper.notification()]);
          break;
        }
        case RoutePaths.FAVORITE: {
          this.router.navigate([RouteHelper.favorite()]);
          break;
        }
        case RoutePaths.ORDERS: {
          this.router.navigate([RouteHelper.orders(ListOrdersRequest_State.STATE_ALL)]);
          break;
        }
        case RoutePaths.REFUND_ACCOUNT: {
          this.router.navigate([RouteHelper.refundAccount()]);
          break;
        }
        case RoutePaths.PROFILE: {
          this.router.navigate([RouteHelper.profile()]);
          break;
        }
      }
    }
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
    });
  }

  trackByFn(
    _: number,
    item: Category_BigCategory | Category_BigCategory_MediumCategory | Category_BigCategory_MediumCategory_SmallCategory
  ): string {
    return item.nameEn;
  }
}
